import { settleDecisionLocally } from '@wix/ai-scheduling-assistant-common-lib/utils';

/**
 * A cached task row as it should read the INSTANT the reader answers one of its questions.
 *
 * Two edits, and leaving either out makes this a no-op:
 *
 * 1. **Drop the answered question** (`settleDecisionLocally`, shared with both phones). Settling writes
 *    `blockedByDecisions` and never the task's status, so there is no status change to wait for — the
 *    released row derives as `Active Task` from the prompts alone.
 *
 * 2. **Drop the server's `displayStatus`.** This is the one that bites. Every read site short-circuits
 *    on the decorated verdict (`if (task.displayStatus) return task.displayStatus.blocked`), so a row
 *    that carries one ignores the prompts entirely and the patch changes nothing on screen. The sibling
 *    `patchTaskDetailInCache` learned this the same way, in its own words: "the verdict outranks
 *    `status` at every read site, so leaving a stale one attached makes the sheet keep its old bucket".
 *    Dropping it hands the row back to local derivation until the server's next frame replaces it.
 *
 * Deliberately a pure function over one row: the caller owns the cache and the rollback, and this stays
 * testable without a QueryClient.
 */
export function optimisticallySettled<T extends { decisionPrompts?: { decisionId: string }[]; displayStatus?: unknown }>(
  task: T,
  decisionId: string,
  /** The option tapped, which for a hand-back also moves the status (see `settleDecisionLocally`). */
  choice?: string,
): T {
  const settled = settleDecisionLocally(task as never, decisionId, choice) as T;
  if (settled === task) {
    // Nothing on this row matched the answered question — leave it identically alone, so a press on one
    // row does not invalidate the verdict of every other row in the list.
    return task;
  }
  const { displayStatus: _staleVerdict, ...rest } = settled;
  return rest as T;
}
